import { translateRule } from '../bootstrap/redact.js';
import { REDACTION_RULES } from '../bootstrap/redaction.js';
import { isSecretVariableName } from '../deployment/secrets.js';
/**
 * Whether a built document carries anything that looks like a credential
 * (IM-54).
 *
 * The acceptance criterion is "no raw credentials are ever handed to a human",
 * and the bundle is the one artifact whose whole purpose is to be handed on.
 * So the property is checked against a *built* bundle rather than argued from
 * the code that builds it — in `bundle.test.ts`, and in the handler test over
 * the real route with secrets seeded into every store it reads.
 *
 * Exported from `shared` for the same reason `cloudAdapterContract` is: the
 * check is used by more than one workspace's tests, and a copy in each would
 * be two definitions of "looks like a secret" to drift apart. It is not wired
 * into the route. The high-entropy rule over-matches by design — a long ECS
 * task ARN can trip it — and a bundle refused at 2am because a stack was given
 * a long name would cost the support engineer the whole picture to save them
 * nothing.
 *
 * Three independent signals, so that one rule going quiet does not make the
 * scan pass:
 *
 * - **A key with a secret's name** — the `isSecretVariableName` shape rule,
 *   which is what keeps secrets out of a run record. Two identifiers it matches
 *   on purpose (`accountKey`, `stateKey`) are named here as benign rather than
 *   the rule being loosened; a stored output that says only `withheld` or
 *   `missing` is the record declining to hold a value; and a flag or a count
 *   holds no text anyone could authenticate with. All three pass.
 * - **A value the redactor would still mask** by the rules that recognise a
 *   credential on sight: an AWS key id, a bearer token, a private-key marker, a
 *   webhook URL, and any long base64 run.
 * - **A value known to be secret**, passed by the caller — the external id, a
 *   registry key, a session token — found anywhere in any string.
 */
/** Names `isSecretVariableName` matches that are identifiers, not secrets. */
export const BENIGN_SECRET_SHAPED_KEYS = new Set(['accountKey', 'stateKey']);
/**
 * A stored output marker that carries no value — `withheld` or `missing`
 * (IM-40). `adminPassword: { state: 'withheld' }` is the record saying the
 * customer holds it, which is the opposite of a leak.
 */
const isValuelessMarker = (value) => typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 1 &&
    (value.state === 'withheld' ||
        value.state === 'missing');
/**
 * Whether anything under a key could hold a credential: a string, somewhere.
 * `suppliesRegistryKey: true` is named like a secret and is a boolean about
 * one, which no one can authenticate with.
 */
const carriesText = (value) => {
    if (typeof value === 'string')
        return value !== '';
    if (Array.isArray(value))
        return value.some(carriesText);
    if (typeof value === 'object' && value !== null)
        return Object.values(value).some(carriesText);
    return false;
};
/** Rules that recognise a credential by its shape, with no label needed. */
const ON_SIGHT = [
    'aws-access-key-id',
    'bearer-token',
    'private-key-header',
    'webhook-url',
    'high-entropy',
];
const DETECTORS = REDACTION_RULES.filter((rule) => ON_SIGHT.includes(rule.name)).map((rule) => {
    const compiled = translateRule(rule);
    // Non-global, so `test` carries no `lastIndex` from one value to the next.
    return {
        name: rule.name,
        pattern: new RegExp(compiled.pattern.source, compiled.pattern.flags.replace('g', '')),
    };
});
export const secretShapedContent = (value, knownSecrets = [], path = []) => {
    if (typeof value === 'string') {
        const findings = [];
        for (const secret of knownSecrets) {
            if (secret !== '' && value.includes(secret))
                findings.push({ path, why: 'carries a known secret' });
        }
        for (const detector of DETECTORS) {
            if (detector.pattern.test(value))
                findings.push({ path, why: `matches ${detector.name}` });
        }
        return findings;
    }
    if (Array.isArray(value)) {
        return value.flatMap((item, index) => secretShapedContent(item, knownSecrets, [...path, index]));
    }
    if (typeof value === 'object' && value !== null) {
        return Object.entries(value).flatMap(([key, child]) => [
            ...(isSecretVariableName(key) &&
                !BENIGN_SECRET_SHAPED_KEYS.has(key) &&
                !isValuelessMarker(child) &&
                carriesText(child)
                ? [{ path: [...path, key], why: 'a key named like a secret' }]
                : []),
            ...secretShapedContent(child, knownSecrets, [...path, key]),
        ]);
    }
    return [];
};
//# sourceMappingURL=secret-scan.js.map